import { Button, PhoneInput } from '@calizahq/react-components-v2'
import { CorporateLeadMutations } from '@sales-app/graphql/mutations/corporateLead.mutations'
import { NewCorporateLead } from '@sales-app/types/CorporateLead'
import { Form, Formik } from 'formik'
import { FC, useState } from 'react'

import { Input } from '@/components'
import { useGraphqlMutation } from '@/hooks/useGraphql'

import { CreateConfirmation } from './CreateConfirmation'
import { formSchema, FormValuesType, initialValues } from './CreateCorporateLead.util'

type Props = {
  refetchOnSuccess: () => void
  toggle: () => void
}

export const CreateCorporateLeadForm: FC<Props> = ({ toggle, refetchOnSuccess }) => {
  const [error, setError] = useState<string>()
  const [corporateLead, setCorporateLead] = useState<NewCorporateLead>()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(CorporateLeadMutations.createCorporateLead(), {
    onSuccess: (res: { createCorporateLead: NewCorporateLead }) => {
      setCorporateLead(res.createCorporateLead)
      refetchOnSuccess && refetchOnSuccess()
    },
    onError: (res: { response: { errors: { message: string }[] } }) => {
      const { response } = res
      if (response.errors && response.errors.length) {
        setError(response.errors[0].message)
      }
    },
  })

  const handleSubmit = (values: FormValuesType) => {
    const { phone, email } = values

    mutate({
      corporateLead: {
        ...values,
        phone: phone || null,
        email: email || null,
      },
    })
  }

  return (
    <>
      {!corporateLead ? (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...initialValues,
          }}
          onSubmit={handleSubmit}
          validationSchema={formSchema}
        >
          {() => {
            return (
              <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
                <p className="leading-nonemt-4 mb-4 font-bold text-header-md">New Corporate Lead</p>
                <div className="flex w-full flex-col md:grid md:grid-cols-2 md:gap-4">
                  <Input
                    className="mb-4 mr-4 w-full"
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    placeholder="First Name"
                    showSuccess={false}
                  />
                  <Input
                    className="mb-4 w-full"
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    placeholder="Last Name"
                    showSuccess={false}
                  />
                </div>

                <Input
                  className="mb-4 w-full"
                  id="companyName"
                  label="Company"
                  name="companyName"
                  placeholder="Company"
                  showSuccess={false}
                />

                <Input
                  className="mb-4 w-full"
                  id="email"
                  label="Email"
                  name="email"
                  placeholder="Email"
                  showSuccess={false}
                  type="email"
                />
                <PhoneInput
                  className="mb-4 w-full"
                  defaultCountry="US"
                  id="phone"
                  international={true}
                  label="Phone"
                  name="phone"
                  placeholder="Phone"
                />

                <Input className="mb-4 w-full" id="numberOfUnits" label="Units" name="numberOfUnits" type="number" />

                {error && <p className=" mb-4 w-full text-p-sm text-text-semantic-error">{error}</p>}
                <div className="mt-4 flex flex-col-reverse justify-between md:flex-row">
                  <Button
                    className="w-full bg-action-secondary md:flex md:w-auto"
                    onClick={toggle}
                    size="small"
                    variant="secondary"
                  >
                    Close
                  </Button>
                  <Button
                    className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                    disabled={isMutationLoading}
                    isLoading={isMutationLoading}
                    size="small"
                    type="submit"
                  >
                    Create new Corporate Lead
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      ) : (
        <CreateConfirmation corporateLead={corporateLead} toggle={toggle} />
      )}
    </>
  )
}
